import { formatBytes, type RecordFileItem } from '@ofmos/shared/files';
import { useState } from 'react';
import { Button, Dialog, Icon } from '../../ui';
import { FilePreview } from './FilePreview';
import { fileIcon, isPreviewable } from './fileItems';
import { useFileDownload } from './useFileDownload';

/**
 * ONE ATTACHMENT, ON A DESKTOP (`docs/decisions/file-attachments.md` D18).
 *
 * A vertical row: the type's glyph, the label, the size, and the two actions. Images
 * additionally carry a BOUNDED preview, and tapping that preview opens the full-size image in
 * a `Dialog`.
 *
 * `DESIGN.md:44-46` binds the surface: a record field section is a DATA SURFACE, so "crisp
 * and opaque. Hairline borders. No blur, no gradients, no glass." The two actions are ghost
 * Buttons - controls INSIDE content, which the component inventory puts on the content layer.
 *
 * THE REMOVE CONFIRMATION IS NOT HERE ANY MORE (wave 1c, finding M1). It used to be a
 * `Dialog` this row owned, and a confirmation owned by the row it removes destroys its own
 * opener: `Dialog` returns focus to the element that had it and skips a detached node
 * (`ui/Dialog/Dialog.tsx`, the rule at `docs/ios26-mechanisms.md`), so every removal dropped
 * focus on `<body>`. Remove now ASKS - the FIELD owns the confirmation, which therefore
 * outlives the row it removes and closes normally afterwards. See `FileList`.
 */
export interface FileRowProps {
  readonly item: RecordFileItem;
  /** Absent means the list is read-only: an archived record, a timeline value, a diff. */
  readonly onRemove?: (() => void) | undefined;
  /** The write that links this file has not settled, so its preview waits (`FilePreview`). */
  readonly pending?: boolean | undefined;
}

export function FileRow({ item, onRemove, pending = false }: FileRowProps) {
  const { busy, download } = useFileDownload(item);
  const [zoomed, setZoomed] = useState(false);

  return (
    <div className="flex items-start gap-3 py-2">
      {isPreviewable(item) ? (
        /**
         * The preview IS the affordance for the full-size view, and it is a real button so a
         * keyboard reaches it. Bounded at 3.5rem so a row of attachments stays a LIST rather
         * than becoming a gallery: D6's cost paragraph is about how much a preview downloads,
         * and this is the half of it a layout can answer.
         */
        <button
          type="button"
          onClick={() => setZoomed(true)}
          className="pressable shrink-0 rounded-control"
          aria-label={`Open ${item.label} full size`}
        >
          <FilePreview item={item} pending={pending} className="h-14 w-14" />
        </button>
      ) : (
        <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-control border border-line bg-surface-sunken text-ink-muted">
          <Icon name={fileIcon(item)} size="1.25rem" />
        </span>
      )}

      <div className="min-w-0 flex-1">
        <p className="truncate text-sm text-ink">{item.label}</p>
        <p className="text-xs text-ink-muted tabular-nums">{formatBytes(item.byteCount)}</p>
        <div className="mt-1 flex flex-wrap items-center gap-1">
          <Button variant="ghost" size="sm" icon="arrow-down" loading={busy} onClick={download}>
            Download
          </Button>
          {onRemove !== undefined && (
            <Button variant="ghost" size="sm" icon="trash" onClick={onRemove}>
              Remove
            </Button>
          )}
        </div>
      </div>

      {/**
       * THE FULL-SIZE IMAGE. `Dialog` in its default `auto` shape, so it is a centred dialog
       * from the desktop breakpoint up and a sheet below it - the grabber, the safe-area
       * inset and the focus return are inherited rather than rebuilt.
       *
       * THIS dialog stays on the row, and the difference from the confirmation is the whole
       * point of the split: looking at a picture does not destroy the button that opened it.
       */}
      <Dialog
        open={zoomed}
        onOpenChange={setZoomed}
        title={item.label}
        width="lg"
        description={formatBytes(item.byteCount)}
        // Reachable inside the table's cell editor too, and for the same reason (see FileList).
        nested
      >
        {/*
          NO UPSCALING. `w-full` was the first version and a screenshot killed it: a small
          image - a logo, a signature, a phone screenshot of a receipt - was blown up to the
          dialog's width and rendered as a blurred slab that misrepresented the file. Bounded
          from above and centred, so a photo fills the dialog and a 64-pixel logo is a
          64-pixel logo.
        */}
        <FilePreview item={item} pending={pending} className="mx-auto max-h-[70dvh] max-w-full" />
      </Dialog>
    </div>
  );
}
